import { FC, ReactNode, useEffect, useRef } from 'react'
import { useRouter } from 'next/router'
import { Loader } from 'rsuite'
import { useClearRefinements, useRefinementList } from 'react-instantsearch'

import BaseFilterPicker from './BaseFilterPicker'
import { ProductAttribute } from '../../../utils/types/Product'
import { createUrlParams } from '../../../lib/params'
import { useApi } from '../../../services/useApi'
import useUrls from '../../../services/useUrls'
import { getPickerValue, handlePickerChange } from './util'
import { getCountryAndLocaleStrings } from '../../../utils/locales'
import { isDefined } from '../../../utils/types/misc'

import styles from '../../../styles/CatalogFilters.module.less'

interface ColorMaterialPickerProps {
  type: string
  label: string
  shouldCloseFilters: boolean
}

const ColorMaterialPicker: FC<ColorMaterialPickerProps> = (props) => {
  const { type, label, shouldCloseFilters } = props

  const { getResource } = useApi()
  const { pushT } = useUrls()
  const initializedRef = useRef(false)
  const { query, asPath, locale } = useRouter()
  const { locale: lang } = getCountryAndLocaleStrings(locale)

  const filterCode = `f_${type}`
  const pickerValue = getPickerValue(query, filterCode)

  const {
    refine: clear,
  } = useClearRefinements({
    includedAttributes: [`${type}.${lang}`],
  })

  const {
    items,
    refine,
    searchForItems,
  } = useRefinementList({
    attribute: `${type}.${lang}`,
    operator: 'or',
    limit: 100,
  })

  const { data: attributeData, isLoading } = getResource<ProductAttribute>('attribute', createUrlParams({
    include: 'attribute',
    filter: {
      '==': {
        field: 'attribute.type',
        value: type,
      },
    },
    page: { limit: '500' },
  }))

  const availableData = attributeData
    .filter((attr) => (
      items.find((item) => item.label === attr['attribute.label'])
    ))
    .map((attr) => {
      const count = items.find((item) => item.label === attr['attribute.label'])?.count || 0
      return {
        label: (
          <>
            {attr['attribute.label']}
            <span className={`${styles.count} ${!count ? styles.disabled : ''}`}>
              {count}
            </span>
          </>
        ),
        value: attr.id,
      }
    })

  useEffect(() => {
    // Refine instantsearch filter items when loading page
    const refined = items.filter((item) => item.isRefined)
    if (!initializedRef.current && refined.length === 0 && pickerValue.length !== 0) {
      initializedRef.current = true
      attributeData
        .filter((attribute) => pickerValue.includes(attribute.id))
        .map((attribute) => attribute['attribute.label'])
        .filter(isDefined)
        .forEach((item) => {
          refine(item)
        })
    }
  }, [items, pickerValue, attributeData, refine])

  const renderMenu = (menu: ReactNode) => {
    if (isLoading) {
      return (
        <Loader
          className={`${styles.loader}`}
          content="Loading..."
        />
      )
    }
    return menu
  }

  return (
    <BaseFilterPicker
      data={availableData}
      filterCode={filterCode}
      placeholder={label}
      value={pickerValue}
      onChange={(value) => {
        handlePickerChange(pushT, query, asPath, filterCode)(value)
        initializedRef.current = true
        const isClear = value.length < pickerValue.length
        const isNew = (id: string) => !pickerValue.includes(id)
        if (isClear) {
          clear()
        }
        attributeData
          .filter((attribute) => (
            value.includes(attribute.id) && (isClear || isNew(attribute.id))))
          .map((attribute) => attribute['attribute.label'])
          .filter(isDefined)
          .forEach(refine)
      }}
      label={pickerValue.length ? label : undefined}
      onSearch={(value) => {
        searchForItems(value)
      }}
      cleanable={false}
      renderMenu={renderMenu}
      onExit={() => searchForItems('')}
      shouldCloseFilters={shouldCloseFilters}
    />
  )
}

export default ColorMaterialPicker
